import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useT } from '../../i18n/LangProvider';

type MealOption = { id: string; labelEn: string; labelFr: string; label?: string };
type Meal = { id: string; nameEn: string; nameFr: string; name?: string; options: MealOption[]; order: number };
type Registration = { email: string; name: string; choices: Record<string, string>; submittedAt: string };

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.error || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return res.json();
}

export default function MealsPage() {
  const { t, lang } = useT();
  const [meals, setMeals] = useState<Meal[]>([]);
  const [existing, setExisting] = useState<Registration | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mealName = (m: Meal) => (lang === 'fr' ? m.nameFr : m.nameEn) || m.name || '';
  const optLabel = (o: MealOption) => (lang === 'fr' ? o.labelFr : o.labelEn) || o.label || '';

  useEffect(() => {
    (async () => {
      try {
        const [cfg, my] = await Promise.all([
          handle<{ meals: Meal[] }>(await fetch('/api/meals-config')),
          handle<{ registration: Registration | null }>(await fetch('/api/meals-my'))
        ]);
        setMeals(cfg.meals);
        setExisting(my.registration);
      } catch (e) { setError((e as Error).message); } finally { setLoading(false); }
    })();
  }, []);

  const submit = async () => {
    for (const m of meals) if (!choices[m.id]) { setError(t('meals.required')); return; }
    setSubmitting(true); setError(null);
    try {
      const r = await handle<{ registration: Registration }>(
        await fetch('/api/meals-register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ choices }) })
      );
      setExisting(r.registration); setDone(true);
    } catch (e) { setError((e as Error).message); } finally { setSubmitting(false); }
  };

  const locked = !!existing;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{t('meals.kicker')}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight text-ink">{t('meals.title')}</motion.h1>
      <p className="text-ink-muted mt-5 max-w-md text-lg">{t('meals.lead')}</p>

      {loading && <p className="text-ink-muted mt-10">…</p>}

      {!loading && locked && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 card p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-sm">✓</span>
            <h2 className="font-display text-2xl text-ink">{t('meals.submitted')}</h2>
          </div>
          <div className="space-y-3">
            {meals.map((m) => (
              <div key={m.id} className="flex justify-between items-center py-3 border-b border-white/[0.06] last:border-0">
                <span className="text-ink-muted">{mealName(m)}</span>
                <span className="text-ink font-medium">{(() => { const o = m.options.find((x) => x.id === existing!.choices[m.id]); return o ? optLabel(o) : (existing!.choices[m.id] || '—'); })()}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink-faint mt-6">{t('meals.submittedNote')}</p>
        </motion.div>
      )}

      {!loading && !locked && (
        <div className="mt-10 space-y-6">
          {meals.length === 0 && <p className="text-ink-muted">{lang === 'fr' ? 'Les options de repas sont en cours de préparation.' : 'Meal options are being finalized.'}</p>}
          {meals.map((m) => (
            <div key={m.id} className="card p-6">
              <p className="font-display text-xl text-ink mb-4">{mealName(m)}</p>
              <div className="space-y-2">
                {m.options.map((o) => {
                  const selected = choices[m.id] === o.id;
                  return (
                    <button key={o.id} onClick={() => setChoices({ ...choices, [m.id]: o.id })}
                      className={`w-full text-left flex items-center gap-3 p-4 rounded-xl border transition-all ${selected ? 'bg-brand-red/[0.08] border-brand-red' : 'bg-bg-base border-white/[0.08] hover:border-white/25'}`}>
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs ${selected ? 'bg-brand-red text-white' : 'border-2 border-ink-faint'}`}>{selected && '✓'}</span>
                      <span className="text-ink">{optLabel(o)}</span>
                    </button>
                  );
                })}
                {m.options.length === 0 && <p className="text-ink-faint text-sm">—</p>}
              </div>
            </div>
          ))}
          {meals.length > 0 && (
            <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-brand-red text-white text-sm rounded-md px-6 py-3 disabled:opacity-40 hover:bg-brand-red-soft transition-colors">
              {submitting ? t('meals.submitting') : t('meals.submit')}
            </button>
          )}
        </div>
      )}

      {error && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">{error}</motion.p>}
      {done && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-emerald-950/50 border border-emerald-700/50 text-emerald-300 text-sm">{t('meals.successBody')}</motion.p>}
    </div>
  );
}
